import * as React from 'react';

import { RefreshButton } from '@/design';

/**
 * A list's one toolbar strip (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.2 item 2), in Conga's order: *Save data* with *Discard* (the writes'), the view picker with
 * its changed mark, *Save view ▾* and *Actions ▾*, then past a spacer *Name contains…*, `Filters
 * (n) ✕`, *Columns*, *Reading pane* and *Refresh* with "updated hh:mm". The pieces are the screen's;
 * this lays them out and draws the two that hold no state of their own.
 *
 * **"updated hh:mm" is the wall clock of the last read** — the page's or a poll's — never
 * survey-api's `asOf`, which it sets two minutes back for the next poll's overlap (§11.4).
 */
export function ListToolbar({
  writes,
  picker,
  changed,
  saveView,
  actions,
  nameBox,
  filters,
  columns,
  pane,
  onRefresh,
  readAt,
  reading
}: {
  /** *Save data (n)* and *Discard* — the writes'. */
  writes?: React.ReactNode;
  picker: React.ReactNode;
  /** The screen differs from the view as opened. */
  changed: boolean;
  saveView?: React.ReactNode;
  actions?: React.ReactNode;
  nameBox: React.ReactNode;
  filters: React.ReactNode;
  columns: React.ReactNode;
  /** *Reading pane* — the pane's. */
  pane?: React.ReactNode;
  onRefresh: () => void;
  readAt: Nullable<Date>;
  reading: boolean;
}) {
  return (
    <div role="toolbar" aria-label="List" className="flex flex-wrap items-center gap-2 text-[0.8125rem]">
      {writes && (
        <>
          {writes}
          <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        </>
      )}
      {picker}
      {changed && (
        <span data-changed="" className="text-xs font-medium text-[var(--color-deep-magenta)]">
          Changed — not saved
        </span>
      )}
      {saveView}
      {actions}
      <span className="flex-1" />
      {nameBox}
      {filters}
      {columns}
      {pane}
      <RefreshButton reading={reading} readAt={readAt} onClick={onRefresh} />
    </div>
  );
}
